<template lang="pug">
.c-onb-admin(v-loading="loading")
  teleport(to="#resource-actions-portal" v-if="mounted")
    el-button(type="primary" @click="openChecklist()")
      fai(type="fal" icon="plus-circle")
      span Add checklist

  p.c-onb-admin__empty(v-if="!loading && checklists.length === 0") No checklists yet

  .c-onb-admin__card(v-for="list in checklists" :key="list.id")
    .c-onb-admin__head
      .c-onb-admin__head-main
        h3.c-onb-admin__title
          | {{ list.title }}
          span.crm-badge.crm-badge--purple {{ list.placement }}
          span.crm-badge.crm-badge--gray(v-if="!list.isEnabled") Disabled
        p.c-onb-admin__meta Key {{ list.key }} · {{ list.tasks.length }} tasks
      .c-onb-admin__head-actions
        el-button(size="small" @click="openChecklist(list)") Edit
        el-button(size="small" type="primary" @click="openTask(list)") Add task
        el-button(size="small" type="danger" plain @click="removeList(list)") Delete

    draggable.c-onb-admin__tasks(
      :model-value="list.tasks"
      item-key="id"
      handle=".c-onb-admin__grip"
      @update:model-value="list.tasks = $event"
      @end="saveOrder(list)"
    )
      template(#item="{ element: task }")
        .c-onb-admin__task(:class="{ 'is-disabled': !task.isEnabled }")
          fai(type="fas" icon="grip-vertical").c-onb-admin__grip
          .c-onb-admin__task-main
            strong {{ task.title }}
            span.c-onb-admin__meta
              |  {{ task.key }}
              template(v-if="task.section")  · {{ task.section }}
              template(v-if="task.completionRule")  · auto: {{ task.completionRule }}
              template(v-else)  · manual
              template(v-if="task.organizationType")  · {{ task.organizationType }}
          .c-onb-admin__task-actions
            el-button(size="small" text @click="openTask(list, task)") Edit
            el-button(size="small" text type="danger" @click="removeTask(task)") Delete

  el-dialog(
    top="6vh"
    v-model="listDialog" :title="listForm.id ? 'Edit checklist' : 'Add checklist'" width="560px"
  )
    el-form.c-onb-admin__form(label-position="top")
      el-form-item(label="Key (letters, digits, _)")
        el-input(v-model="listForm.key" :disabled="!!listForm.id")
      el-form-item(label="Placement")
        el-select(v-model="listForm.placement" :disabled="!!listForm.id" allow-create filterable)
          el-option(v-for="p in placements" :key="p" :label="p" :value="p")
      el-form-item(label="Title")
        el-input(v-model="listForm.title")
      el-form-item(label="Subtitle")
        el-input(v-model="listForm.subtitle")
      el-form-item(label="Pro tip")
        el-input(v-model="listForm.proTip" type="textarea" :rows="2")
      el-form-item(label="Completion title")
        el-input(v-model="listForm.completionTitle")
      el-form-item(label="Completion message")
        el-input(v-model="listForm.completionMessage" type="textarea" :rows="2")
      el-form-item(label="Enabled")
        el-switch(v-model="listForm.isEnabled")
    template(#footer)
      el-button(@click="listDialog = false") Cancel
      el-button(type="primary" :loading="saving" @click="saveList") Save

  el-dialog(
    top="6vh"
    v-model="taskDialog"
    :title="taskForm.id ? 'Edit task' : 'Add task'"
    width="620px"
  )
    el-form.c-onb-admin__form(label-position="top")
      el-form-item(label="Key (letters, digits, _)")
        el-input(v-model="taskForm.key" :disabled="!!taskForm.id")
      el-form-item(label="Section heading (empty = none)")
        el-input(v-model="taskForm.section")
      el-form-item(label="Title")
        el-input(v-model="taskForm.title")
      el-form-item(label="Tooltip")
        el-input(v-model="taskForm.tooltip" type="textarea" :rows="3")
      el-form-item(label="Details")
        el-input(v-model="taskForm.details" type="textarea" :rows="2")
      el-form-item(label="Media URL (image or video, http/https)")
        el-input(v-model="taskForm.mediaUrl")
      el-form-item(label="Action button label")
        el-input(v-model="taskForm.actionLabel")
      el-form-item(label="Action type")
        el-select(v-model="taskForm.actionType" clearable)
          el-option(v-for="t in actionTypes" :key="t" :label="t" :value="t")
      el-form-item(label="Action target (CMS path, URL, panel name or deep link)")
        el-input(
          v-model="taskForm.actionTarget"
          :placeholder="taskForm.actionType === 'ROUTE' ? '/commerce/stickers (empty = default page)' : ''"
        )
      el-form-item(label="Completes when…")
        el-select(
          v-model="taskForm.completionRule" clearable placeholder="Manual (org marks it done)"
        )
          el-option(
            v-for="r in rules" :key="r.key" :label="`${r.key} – ${r.description}`" :value="r.key"
          )
      el-form-item(label="Organization type")
        el-select(v-model="taskForm.organizationType" clearable placeholder="All")
          el-option(label="Nonprofit" value="NONPROFIT")
          el-option(label="For-profit" value="FORPROFIT")
      el-form-item(label="Enabled")
        el-switch(v-model="taskForm.isEnabled")
    template(#footer)
      el-button(@click="taskDialog = false") Cancel
      el-button(type="primary" :loading="saving" @click="saveTask") Save
</template>

<script lang="ts">
import {
  defineComponent, onMounted, reactive, ref,
} from 'vue';
import draggable from 'vuedraggable';
import { ElMessage, ElMessageBox } from 'element-plus';
import globalShared from '@/shared';
import {
  findAllOnboardingChecklists,
  onboardingCompletionRules,
  mutateOnboardingChecklist,
  removeOnboardingChecklist,
  mutateOnboardingTask,
  removeOnboardingTask,
  reorderOnboardingTasks,
} from '@/graphs/gql';

const LIST_FIELDS = ['key', 'placement', 'title', 'subtitle', 'proTip',
  'completionTitle', 'completionMessage'];
const TASK_FIELDS = ['key', 'section', 'title', 'tooltip', 'details', 'mediaUrl',
  'actionLabel', 'actionType', 'actionTarget', 'completionRule', 'organizationType'];

const blank = (fields: string[]) => fields.reduce((acc: any, f) => ({ ...acc, [f]: '' }), {});

// Create: leave empty fields out. Edit: "" clears a field, so send everything.
function buildInput(form: any, fields: string[]) {
  const input: any = { isEnabled: form.isEnabled };
  if (form.id) input.id = form.id;
  fields.forEach((f) => {
    const value = form[f] == null ? '' : String(form[f]).trim();
    if (form.id || value) input[f] = value;
  });
  return input;
}

export default defineComponent({
  name: 'OnboardingAdmin',
  components: { draggable },
  setup() {
    const { makeGraphRequest } = globalShared();
    const mounted = ref(false);
    const loading = ref(false);
    const saving = ref(false);
    const checklists = ref<any[]>([]);
    const rules = ref<any[]>([]);
    const placements = ['DASHBOARD', 'STICKER_PORTAL'];
    const actionTypes = ['ROUTE', 'URL', 'IFRAME', 'SIDEBAR', 'DEEPLINK'];

    const listDialog = ref(false);
    const listForm = reactive<any>({ ...blank(LIST_FIELDS), isEnabled: true });
    const taskDialog = ref(false);
    const taskForm = reactive<any>({ ...blank(TASK_FIELDS), isEnabled: true });

    async function load() {
      loading.value = true;
      try {
        const [lists, ruleList] = await Promise.all([
          makeGraphRequest('query', findAllOnboardingChecklists, {}),
          makeGraphRequest('query', onboardingCompletionRules, {}),
        ]);
        if (lists.errors) throw new Error(lists.errors[0].message);
        checklists.value = lists.data.findAllOnboardingChecklists;
        rules.value = ruleList.data?.onboardingCompletionRules || [];
      } catch (e: any) {
        ElMessage.error(e.message || 'Could not load checklists');
      } finally {
        loading.value = false;
      }
    }

    async function run(doc: any, variables: any, success?: string) {
      saving.value = true;
      try {
        const response = await makeGraphRequest('mutation', doc, variables);
        if (response.errors) throw new Error(response.errors[0].message);
        if (success) ElMessage.success(success);
        await load();
        return true;
      } catch (e: any) {
        ElMessage.error(e.message || 'Something went wrong');
        return false;
      } finally {
        saving.value = false;
      }
    }

    function openChecklist(list?: any) {
      Object.assign(listForm, { ...blank(LIST_FIELDS), id: undefined, isEnabled: true }, list
        ? { ...list, ...Object.fromEntries(LIST_FIELDS.map((f) => [f, list[f] || ''])) } : {});
      listDialog.value = true;
    }

    async function saveList() {
      if (await run(mutateOnboardingChecklist, { input: buildInput(listForm, LIST_FIELDS) }, 'Saved')) {
        listDialog.value = false;
      }
    }

    function openTask(list: any, task?: any) {
      Object.assign(
        taskForm,
        { ...blank(TASK_FIELDS), id: undefined, isEnabled: true },
        task ? { ...task, ...Object.fromEntries(TASK_FIELDS.map((f) => [f, task[f] || ''])) } : {},
        { checklistId: list.id },
      );
      taskDialog.value = true;
    }

    async function saveTask() {
      const input = buildInput(taskForm, TASK_FIELDS);
      input.checklistId = taskForm.checklistId;
      if (await run(mutateOnboardingTask, { input }, 'Saved')) taskDialog.value = false;
    }

    async function removeList(list: any) {
      await ElMessageBox.confirm(
        `Delete "${list.title}" and its tasks? Every org's progress is deleted too.`,
        'Delete checklist',
        { type: 'warning', confirmButtonText: 'Delete' },
      );
      run(removeOnboardingChecklist, { checklistId: list.id }, 'Deleted');
    }

    async function removeTask(task: any) {
      await ElMessageBox.confirm(
        `Delete "${task.title}"? Every org's progress on it is deleted too.`,
        'Delete task',
        { type: 'warning', confirmButtonText: 'Delete' },
      );
      run(removeOnboardingTask, { taskId: task.id }, 'Deleted');
    }

    function saveOrder(list: any) {
      const taskIds = list.tasks.map((t: any) => t.id);
      run(reorderOnboardingTasks, { checklistId: list.id, taskIds });
    }

    onMounted(() => {
      mounted.value = true;
      load();
    });

    return {
      mounted,
      loading,
      saving,
      checklists,
      rules,
      placements,
      actionTypes,
      listDialog,
      listForm,
      taskDialog,
      taskForm,
      openChecklist,
      saveList,
      openTask,
      saveTask,
      removeList,
      removeTask,
      saveOrder,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/views/manage-users-and-organizations/crm-list';

@include crm-cells;

.c-onb-admin {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  :deep(svg) {
    width: 1em;
    height: 1em;
  }

  &__form {
    max-height: 62vh;
    overflow-y: auto;
    padding-right: 8px;
  }

  &__empty {
    @include crm-card;
    padding: 3rem 2rem;
    text-align: center;
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
  }

  &__card {
    @include crm-card;
    padding: 1.5rem;
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: $foreground;
  }

  &__meta {
    margin: 4px 0 0;
    font-size: 0.75rem;
    color: $gray-600;
  }

  &__tasks {
    margin-top: 12px;
  }

  &__task {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 4px;
    border-top: 1px solid $border;
    font-size: 0.875rem;

    &.is-disabled {
      opacity: 0.5;
    }
  }

  &__task-main {
    flex: 1;

    strong {
      font-weight: 500;
      color: $foreground;
    }

    .c-onb-admin__meta {
      margin-left: 8px;
    }
  }

  &__grip {
    cursor: grab;
    color: $gray-400;
  }

  &__head-main { min-width: 0; }

  &__head-actions {
    display: flex;
    flex: none;
    gap: 8px;
  }

  &__title { flex-wrap: wrap; }

  &__task-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: none;

    .el-button { margin: 0; }
  }

  @media (max-width: 768px) {
    &__card { padding: 1rem; }

    &__head {
      flex-direction: column;
      gap: 0.75rem;
    }

    &__head-actions {
      display: flex;
      gap: 8px;
      width: 100%;

      .el-button { flex: 1 1 0; margin: 0; }
    }

    &__task {
      align-items: flex-start;
      gap: 8px;
      padding: 10px 0;
    }

    &__grip { margin-top: 3px; }

    &__task-main {
      min-width: 0;

      strong { display: block; }

      .c-onb-admin__meta {
        display: block;
        margin: 2px 0 0;
        overflow-wrap: anywhere;
      }
    }

    // Edit / Delete stacked so the title keeps the width
    &__task-actions {
      flex-direction: column;
      align-items: flex-end;
      gap: 2px;

      .el-button { padding: 4px 6px; height: auto; min-height: 0; }
    }
  }
}
</style>
